import React, { useEffect, useMemo, useState } from "react";
import { muAxios } from "../../api/axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Link, useParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { PageSkeleton } from "../../components/Skeleton";

function Chapter() {
  const { user } = useAuthContext();
  const { notifyError } = useToast();

  const { course, chapter } = useParams();

  const [logo, setLogo] = useState("");
  const [title, setTitle] = useState("");

  const [lastLessonCount, setLastLessonCount] = useState(0);
  const [vidOrder, setVidOrder] = useState([]);
  const [allLesson, setAllLesson] = useState([]);

  const [progress, setProgress] = useState(0);

  const [continueTarget, setContinueTarget] = useState(null);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [pageNotFound, setPageNotFound] = useState(false);

  const headers = useMemo(
    () => ({ headers: { accessToken: user?.token } }),
    [user?.token]
  );

  // Load base chapter page data
  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!user?.token || !course || !chapter) return;

      setLoading(true);
      setPageError("");
      setPageNotFound(false);

      try {
        const [pRes, courseRes, chapRes, lessonRes] = await Promise.all([
          muAxios.get(`/api/mu/get/${course}/progress`, headers),
          muAxios.get(`/api/mu/get/course/${course}`, headers),
          muAxios.get(`/api/mu/get/chapter/${course}/${chapter}`, headers),
          muAxios.get(`/api/mu/get/lesson/all/${course}/${chapter}`, headers),
        ]);

        if (!mounted) return;

        // progress (course-level currentLesson)
        setProgress(Number(pRes?.data?.progress || 0));

        // course info
        const cjson = courseRes?.data;
        if (cjson) {
          setLogo(process.env.REACT_APP_MU + cjson.logo);
          setTitle(cjson.title || "");
        }

        // chapter info
        const chjson = chapRes?.data;
        const lessonList = Array.isArray(lessonRes?.data) ? lessonRes.data : [];
        const chapterVidOrder = Array.isArray(chjson?.vidOrder) ? chjson.vidOrder : [];
        const hasChapterPayload =
          !!chjson &&
          typeof chjson === "object" &&
          (chapterVidOrder.length > 0 ||
            lessonList.length > 0 ||
            Number(chjson?.lastLessonCount || 0) > 0);

        if (!hasChapterPayload) {
          setPageNotFound(true);
          setLastLessonCount(0);
          setVidOrder([]);
          setAllLesson([]);
          setContinueTarget(null);
          return;
        }

        if (chjson) {
          setLastLessonCount(Number(chjson.lastLessonCount || 0));
          setVidOrder(chapterVidOrder);
        }

        // lesson list
        setAllLesson(lessonList);
      } catch (e) {
        if (!mounted) return;
        setPageError("Unable to load chapter content. Please refresh and try again.");
        notifyError("Failed to load chapter.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => (mounted = false);
  }, [user?.token, course, chapter, headers, notifyError]);

  // Determine first lesson in this chapter (important for chapter-level unlock)
  const chapterFirstLesson = useMemo(() => {
    if (!allLesson?.length) return null;
    return Math.min(...allLesson.map((x) => Number(x.lesson)));
  }, [allLesson]);

  const chapterTotalLessons = useMemo(() => {
    if (allLesson?.length) return allLesson.length;
    if (Array.isArray(vidOrder) && vidOrder.length) return vidOrder.length;
    return Number(lastLessonCount || 0);
  }, [allLesson, vidOrder, lastLessonCount]);

  // Chapter-level progress: only counts if course progress has reached this chapter's first lesson
  const chapterProgressMeta = useMemo(() => {
    const p = Number(progress || 0);
    const first = Number(chapterFirstLesson || 0);
    const total = Number(chapterTotalLessons || 0);

    if (!first || total === 0 || p < first) {
      return { first, total, completed: 0, percent: 0, inThisChapter: false };
    }

    // completed lessons within chapter:
    // if progress = 4 and first=4 => completed 0 (you're on lesson 4)
    // if progress = 6 and first=4 => completed 2
    const completed = Math.max(0, Math.min(total, p - first));
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { first, total, completed, percent, inThisChapter: true };
  }, [progress, chapterFirstLesson, chapterTotalLessons]);

  // Resolve a real lesson target for the Continue button.
  // If DB progress overshoots the last lesson, fall back to the last valid lesson.
  useEffect(() => {
    let mounted = true;

    const resolveContinueTarget = async () => {
      if (!user?.token || !course) return;

      const p = Number(progress || 0);
      if (!p || p < 1) {
        if (mounted) setContinueTarget(null);
        return;
      }

      try {
        const chapRes = await muAxios.get(`/api/mu/get/chapter/${course}`, headers);
        const chapters = chapRes?.data?.chapter || [];
        let fallbackTarget = null;

        for (const ch of chapters) {
          const chNo = Number(ch.chapter);
          const res = await muAxios.get(`/api/mu/get/chapter/${course}/${chNo}`, headers);
          const orders = (res?.data?.vidOrder || []).map(Number);
          const highestLesson = orders.length ? Math.max(...orders) : null;

          if (
            highestLesson != null &&
            (!fallbackTarget || highestLesson > Number(fallbackTarget.lesson))
          ) {
            fallbackTarget = { chapter: chNo, lesson: highestLesson };
          }

          if (orders.includes(p)) {
            if (mounted) setContinueTarget({ chapter: chNo, lesson: p });
            return;
          }
        }

        if (mounted) {
          setContinueTarget(
            fallbackTarget || {
              chapter: Number(chapter),
              lesson: p,
            }
          );
        }
      } catch (e) {
        if (mounted) {
          setContinueTarget({
            chapter: Number(chapter),
            lesson: p,
          });
        }
      }
    };

    resolveContinueTarget();
    return () => (mounted = false);
  }, [user?.token, course, progress, headers, chapter]);

  const getLessonStatus = (lessonNo) => {
    const p = Number(progress || 0);
    const first = Number(chapterFirstLesson || 0);

    // If user hasn't reached this chapter yet, all locked
    if (first && p < first) return { label: "Locked", klass: "bg-light text-muted border" };

    if (p < lessonNo) return { label: "Locked", klass: "bg-light text-muted border" };
    if (p === lessonNo) return { label: "In progress", klass: "bg-primary" };
    return { label: "Completed", klass: "bg-success" };
  };

  const canOpenLesson = (lessonNo) => {
    const p = Number(progress || 0);
    const first = Number(chapterFirstLesson || 0);
    if (first && p < first) return false;
    return p >= lessonNo;
  };

  if (loading) return <PageSkeleton variant="table" rows={6} cols={4} />;

  if (!loading && pageNotFound) {
    return (
      <div className="container py-6">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/courses">Courses</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/courses/${course}`}>{title || course}</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Chapter {chapter}
            </li>
          </ol>
        </nav>

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <div className="fw-semibold mb-2" style={{ fontSize: 22 }}>
              Page not found
            </div>
            <div className="text-muted mx-auto" style={{ fontSize: 14, maxWidth: 520 }}>
              Chapter {chapter} does not exist for this course.
            </div>

            <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
              <Link className="btn btn-outline-secondary" to="/courses">
                Back to Courses
              </Link>
              <Link className="btn btn-dark" to={`/courses/${course}`}>
                Back to Course
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/courses">Courses</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/courses/${course}`}>{title || course}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Chapter {chapter}
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          {/* Logo ratio-safe */}
          <div
            className="rounded-4 border bg-white d-flex align-items-center justify-content-center"
            style={{ width: 90, height: 70, padding: 8 }}
          >
            {logo ? (
              <img
                src={logo}
                alt={title}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            ) : (
              <span className="text-muted" style={{ fontSize: 12 }}>
                No logo
              </span>
            )}
          </div>

          <div>
            <div className="fw-semibold" style={{ fontSize: 18 }}>
              {title || "Course"}
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Chapter {chapter} • {chapterTotalLessons || 0} lesson(s)
            </div>
          </div>
        </div>

        <div className="text-muted" style={{ fontSize: 13 }}>
          {loading
            ? "Loading..."
            : `Progress: ${chapterProgressMeta.completed}/${chapterProgressMeta.total} (${chapterProgressMeta.percent}%)`}
        </div>
      </div>

      {/* Error */}
      {pageError && (
        <div className="alert alert-danger" role="alert">
          {pageError}
        </div>
      )}

      {/* Main */}
      <div className="row g-3">
        {/* Left: lesson list */}
        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="fw-semibold">Lessons</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {loading ? "Loading..." : "Select a lesson to continue"}
                </div>
              </div>

              {loading ? (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Loading lessons...
                </div>
              ) : allLesson.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No lessons found for this chapter.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-hover mb-0">
                    <thead>
                      <tr className="text-muted" style={{ fontSize: 13 }}>
                        <th style={{ width: 90 }}>Lesson</th>
                        <th>Topic</th>
                        <th style={{ width: 140 }}>Status</th>
                        <th style={{ width: 110 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {allLesson.map((v) => {
                        const lessonNo = Number(v.lesson);
                        const st = getLessonStatus(lessonNo);

                        return (
                          <tr key={`${v.id || v.lesson}-${v.vidOrder || ""}`}>
                            <td className="fw-semibold">{lessonNo}</td>
                            <td>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>
                                {v.lessonTitle}
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {v.chapterTitle}
                              </div>
                            </td>
                            <td>
                              <span className={`badge rounded-pill ${st.klass}`}>
                                {st.label}
                              </span>
                            </td>
                            <td className="text-end">
                            {canOpenLesson(lessonNo) ? (
                                <Link
                                className="btn btn-sm btn-dark"
                                to={`/courses/${course}/${chapter}/${lessonNo}`}
                                >
                                Open
                                </Link>
                            ) : (
                                <button className="btn btn-sm btn-outline-secondary" disabled>
                                <i className="bi bi-lock-fill me-1" />
                                Locked
                                </button>
                            )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: overview */}
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="fw-semibold mb-2">Overview</div>

              <div className="text-muted" style={{ fontSize: 13 }}>
                Continue where you left off. Locked lessons will unlock as you complete the current lesson.
              </div>

              <div className="mt-3">
                <div className="d-flex justify-content-between text-muted" style={{ fontSize: 12 }}>
                  <span>Progress</span>
                  <span>{chapterProgressMeta.percent}%</span>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${chapterProgressMeta.percent}%` }}
                  />
                </div>
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  Completed {chapterProgressMeta.completed} of {chapterProgressMeta.total} lesson(s)
                </div>
              </div>

              {/* Quick actions */}
              <div className="d-grid gap-2 mt-4">
                <Link to={`/courses/${course}`} className="btn btn-outline-secondary">
                  Back to Chapters
                </Link>

                {/* Continue button: go to real chapter that contains current progress lesson */}
                {Number(progress || 0) >= 1 && continueTarget && (
                  <Link
                    to={`/courses/${course}/${continueTarget.chapter}/${continueTarget.lesson}`}
                    className="btn btn-dark"
                  >
                    Continue Lesson {continueTarget.lesson}
                  </Link>
                )}
              </div>

              {/* Quick jump */}
              {Array.isArray(vidOrder) && vidOrder.length > 0 && (
                <div className="mt-4">
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                    Quick jump
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {vidOrder.map((n) => {
                      const num = Number(n);
                      const chapterNotReached =
                        chapterFirstLesson && Number(progress || 0) < Number(chapterFirstLesson);
                      const disabled = chapterNotReached || Number(progress || 0) < num;

                      return (
                        <Link
                          key={num}
                          className={`btn btn-sm ${
                            disabled ? "btn-outline-secondary disabled" : "btn-outline-dark"
                          }`}
                          to={`/courses/${course}/${chapter}/${num}`}
                        >
                          {num}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="text-muted mt-3" style={{ fontSize: 12 }}>
            Tip: If a lesson is locked, complete your current lesson to unlock the next one.
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chapter;
